import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { decodeVin, isValidVin, type DecodedVin } from '@/lib/vin'

type OcrResponse = {
  vin: string | null
  confidence: 'high' | 'medium' | 'low'
  photo_r2_key: string
  reason?: string
}

type ConflictPayload = {
  error: 'vin_conflict'
  existing_vehicle: {
    id: string
    year: number | null
    make: string | null
    model: string | null
    customer_id: string | null
    owner_name: string | null
  }
}

function isConflictPayload(value: unknown): value is ConflictPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { error?: unknown }).error === 'vin_conflict'
  )
}

export function VinPhotoUploadModal({
  vehicleId,
  open,
  onOpenChange,
}: {
  vehicleId: string
  open: boolean
  onOpenChange: (next: boolean) => void
}) {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)

  const [ocr, setOcr] = useState<OcrResponse | null>(null)
  const [ocrError, setOcrError] = useState<string | null>(null)

  // Mechanic-editable VIN. Pre-filled by OCR; user can correct it on
  // medium/low confidence reads.
  const [vin, setVin] = useState('')

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [conflict, setConflict] = useState<
    ConflictPayload['existing_vehicle'] | null
  >(null)

  // Reset whenever the dialog opens/closes — never leave stale OCR or
  // photo state behind. Pattern A (You Might Not Need an Effect):
  // snapshot the previous `open` and in-render setState on the close
  // transition.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (!open) {
      setFile(null)
      setPreviewUrl(null)
      setDragActive(false)
      setOcr(null)
      setOcrError(null)
      setVin('')
      setSubmitError(null)
      setConflict(null)
    }
  }

  // Manage the preview blob URL lifecycle. createObjectURL / revoke is
  // a real browser-resource pairing that must live in an effect; the
  // null branch is a derived-in-render reset (Pattern A) so the effect
  // only runs when a file actually exists.
  const [prevFile, setPrevFile] = useState<File | null>(null)
  if (file !== prevFile) {
    setPrevFile(file)
    if (!file && previewUrl !== null) setPreviewUrl(null)
  }
  useEffect(() => {
    if (!file) return
    const url = URL.createObjectURL(file)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- paired with revokeObjectURL cleanup; genuine browser-resource lifecycle
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Re-decode whenever the VIN becomes 17-char and valid. Pattern C
  // (TanStack useQuery): isFetching becomes the "decoding" indicator,
  // error supplies the error string — no setState-in-effect kickoff.
  const trimmedVin = vin.trim().toUpperCase()
  const vinIsValid = isValidVin(trimmedVin)
  const vinDecodeQuery = useQuery({
    queryKey: ['vin-decode', trimmedVin],
    enabled: vinIsValid,
    queryFn: ({ signal }) => decodeVin(trimmedVin, { signal }),
  })
  const decoded: DecodedVin | null =
    vinIsValid && vinDecodeQuery.data ? vinDecodeQuery.data : null
  const vinDecoding = vinIsValid && vinDecodeQuery.isFetching
  const vinError =
    vinIsValid && vinDecodeQuery.error
      ? vinDecodeQuery.error instanceof Error
        ? vinDecodeQuery.error.message
        : t('from_photo.vin_decode_error')
      : null

  const ocrMutation = useMutation({
    mutationFn: async (photo: File) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const fd = new FormData()
      fd.append('photo', photo)

      const res = await fetch(`/api/vehicles/${vehicleId}/vin-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as OcrResponse
    },
    onSuccess: (data) => {
      setOcr(data)
      setOcrError(null)
      if (data.vin) setVin(data.vin)
    },
    onError: (err) => {
      setOcr(null)
      setOcrError(
        err instanceof Error ? err.message : t('vin_modal.error.ocr_failed'),
      )
    },
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const payload = {
        vin: vin.trim().toUpperCase(),
        year: decoded?.year ?? null,
        make: decoded?.make ?? null,
        model: decoded?.model ?? null,
        engine: decoded?.engine ?? null,
        photo_r2_key: ocr?.photo_r2_key ?? null,
      }

      const res = await fetch(`/api/vehicles/${vehicleId}/from-photo`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (res.status === 409) {
        const body = await res.json().catch(() => null)
        if (isConflictPayload(body)) {
          throw new ConflictError(body.existing_vehicle)
        }
        throw new Error(t('vin_modal.error.conflict_generic'))
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { ok: true; id: string; vin: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      onOpenChange(false)
    },
    onError: (err) => {
      if (err instanceof ConflictError) {
        setConflict(err.existing)
        setSubmitError(null)
        return
      }
      setSubmitError(
        err instanceof Error ? err.message : t('vin_modal.error.save_failed'),
      )
    },
  })

  function handleFileSelected(f: File | null) {
    setFile(f)
    setOcr(null)
    setOcrError(null)
    setVin('')
    setConflict(null)
    setSubmitError(null)
    if (f) ocrMutation.mutate(f)
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragActive(false)
    const f = e.dataTransfer.files?.[0] ?? null
    if (f && f.type.startsWith('image/')) handleFileSelected(f)
  }

  function handlePickDifferentPhoto() {
    setConflict(null)
    setFile(null)
    setOcr(null)
    setVin('')
  }

  const submitting = saveMutation.isPending
  const canSave = isValidVin(trimmedVin) && !submitting

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!isValidVin(trimmedVin)) {
      setSubmitError(t('from_photo.error.invalid_vin'))
      return
    }
    saveMutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (!submitting ? onOpenChange(next) : null)}
    >
      <DialogContent className="sm:max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('vin_modal.title')}</DialogTitle>
          <DialogDescription>{t('vin_modal.subtitle')}</DialogDescription>
        </DialogHeader>

        {conflict ? (
          <ConflictView
            conflict={conflict}
            onPickDifferent={handlePickDifferentPhoto}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <form onSubmit={handleSave} className="space-y-5">
            {/* ─── Upload ────────────────────────────────────────── */}
            <section className="space-y-2">
              <div className="text-[13px] font-semibold text-muted-foreground">
                {t('vin_modal.upload.header')}
              </div>

              {previewUrl ? (
                <div className="space-y-3">
                  <img
                    src={previewUrl}
                    alt={t('from_photo.upload.preview_alt')}
                    className="max-h-56 w-full rounded-[10px] border border-border object-contain"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting}
                    >
                      <Upload className="size-4" />
                      {t('from_photo.upload.replace')}
                    </Button>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor={`vin-photo-modal-${vehicleId}`}
                  onClick={(e) => {
                    // Same iOS Safari workaround as VehicleFromPhoto.tsx —
                    // labelFor → input.click() bridge drops on sr-only
                    // inputs intermittently. Calling .click() on the ref
                    // makes the picker open reliably.
                    e.preventDefault()
                    fileInputRef.current?.click()
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setDragActive(true)
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={onDrop}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed bg-muted/30 px-4 py-6 text-center transition-[background-color,border-color] ${
                    dragActive
                      ? 'border-foreground/40 bg-muted'
                      : 'border-border [@media(hover:hover)_and_(pointer:fine)]:hover:border-foreground/30 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted/60'
                  }`}
                >
                  <Camera className="size-7 text-ink-3" />
                  <div className="text-sm font-medium text-foreground">
                    {t('from_photo.upload.tap_or_drop')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('from_photo.upload.formats')}
                  </div>
                </label>
              )}

              <input
                ref={fileInputRef}
                id={`vin-photo-modal-${vehicleId}`}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null
                  handleFileSelected(f)
                  e.target.value = ''
                }}
              />

              {ocrMutation.isPending ? (
                <div className="rounded-[10px] border border-border bg-muted p-2.5 text-xs text-muted-foreground">
                  {t('from_photo.extracting')}
                </div>
              ) : null}

              {ocrError ? (
                <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
                  {ocrError}
                </div>
              ) : null}

              {ocr && ocr.vin === null ? (
                <div className="rounded-[10px] border border-attention-line bg-attention-soft p-2.5 text-xs text-attention-text">
                  {t('from_photo.no_vin_detected')}
                </div>
              ) : null}

              {ocr && ocr.vin && ocr.confidence !== 'high' ? (
                <div className="rounded-[10px] border border-attention-line bg-attention-soft p-2.5 text-xs text-attention-text">
                  {t('from_photo.confidence_warning', {
                    confidence: tCommon(`confidence.${ocr.confidence}`),
                  })}
                </div>
              ) : null}
            </section>

            {/* ─── VIN review ───────────────────────────────────── */}
            {ocr || vin ? (
              <section className="space-y-2">
                <div className="text-[13px] font-semibold text-muted-foreground">
                  {t('vin_modal.review.header')}
                </div>
                <Label htmlFor="vin-modal-input">
                  {t('from_photo.vin.label')}
                </Label>
                <Input
                  id="vin-modal-input"
                  value={vin}
                  onChange={(e) =>
                    setVin(e.target.value.toUpperCase().replace(/\s+/g, ''))
                  }
                  placeholder={t('from_photo.vin.placeholder')}
                  maxLength={17}
                  disabled={submitting}
                  className="font-mono uppercase"
                />
                {vinDecoding ? (
                  <p className="text-xs text-muted-foreground">
                    {t('from_photo.vin.decoding')}
                  </p>
                ) : null}
                {vinError ? (
                  <p className="text-xs text-destructive">{vinError}</p>
                ) : null}
                {decoded ? (
                  <div className="grid gap-2 rounded-[10px] border border-border bg-muted/40 p-3 text-xs sm:grid-cols-4">
                    <DecodedField
                      label={t('from_photo.vin.year')}
                      value={decoded.year ? String(decoded.year) : null}
                    />
                    <DecodedField
                      label={t('from_photo.vin.make')}
                      value={decoded.make}
                    />
                    <DecodedField
                      label={t('from_photo.vin.model')}
                      value={decoded.model}
                    />
                    <DecodedField
                      label={t('from_photo.vin.engine')}
                      value={decoded.engine}
                    />
                  </div>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  {t('vin_modal.review.note')}
                </p>
              </section>
            ) : null}

            {submitError ? (
              <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
                {submitError}
              </div>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {t('vin_modal.cancel')}
              </Button>
              <Button type="submit" disabled={!canSave}>
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    {t('vin_modal.saving')}
                  </>
                ) : (
                  t('vin_modal.save')
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Custom Error so the saveMutation onError handler can branch cleanly
// between conflict and generic failure without parsing a free-form
// message string. The 409 path returns the full conflicting-vehicle
// payload so the ConflictView can render it without a second request.
class ConflictError extends Error {
  existing: ConflictPayload['existing_vehicle']
  constructor(existing: ConflictPayload['existing_vehicle']) {
    super('vin_conflict')
    this.name = 'ConflictError'
    this.existing = existing
  }
}

function ConflictView({
  conflict,
  onPickDifferent,
  onCancel,
}: {
  conflict: ConflictPayload['existing_vehicle']
  onPickDifferent: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation('vehicles')
  const existingLabel =
    [conflict.year, conflict.make, conflict.model].filter(Boolean).join(' ') ||
    t('detail.title_fallback')
  return (
    <div className="space-y-4">
      <div className="rounded-[10px] border border-attention-line bg-attention-soft p-3 text-sm text-attention-text">
        {t('vin_modal.conflict.body')}
      </div>
      <div className="rounded-[10px] border border-border bg-background p-3">
        <div className="text-[13px] font-semibold text-muted-foreground">
          {t('vin_modal.conflict.existing_header')}
        </div>
        <div className="mt-1 text-sm font-semibold text-foreground">
          {existingLabel}
        </div>
        {conflict.owner_name ? (
          <div className="text-xs text-muted-foreground">
            {t('vin_modal.conflict.owner_prefix')} {conflict.owner_name}
          </div>
        ) : null}
        <Link
          to={`/vehicles/${conflict.id}`}
          className="mt-2 inline-flex min-h-11 items-center text-xs font-semibold text-foreground underline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
        >
          {t('vin_modal.conflict.open_existing')}
        </Link>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('vin_modal.conflict.cancel')}
        </Button>
        <Button type="button" variant="outline" onClick={onPickDifferent}>
          {t('vin_modal.conflict.pick_different')}
        </Button>
      </DialogFooter>
    </div>
  )
}

function DecodedField({
  label,
  value,
}: {
  label: string
  value: string | null
}) {
  return (
    <div>
      <div className="text-[12px] font-medium text-ink-3">
        {label}
      </div>
      <div className="mt-0.5 text-xs font-medium text-foreground">
        {value ?? '—'}
      </div>
    </div>
  )
}
